import React, { useEffect, useState } from "react";
import { Navbar, MobileNav } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEntitlements } from "@/context/EntitlementsContext";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Crown, Sparkles, Info } from "lucide-react";
import { toast } from "sonner";

export default function Plans() {
  const { user } = useAuth();
  const { plan: myPlan, isPro } = useEntitlements();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const audience = user?.role === "business" ? "business" : "creator";

  useEffect(() => {
    api.get("/billing/config").then((r) => setConfig(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex min-h-screen items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;

  const sym = config?.currency_symbol || "£";
  const paymentsOn = config?.payments_enabled;
  const plans = config?.plans || {};
  const features = config?.features || {};
  const freeKey = `${audience}_free`;
  const proKey = `${audience}_pro`;

  // Features relevant to this audience, in display order.
  const relevant = Object.entries(features).filter(([, f]) => f.audience === "any" || f.audience === audience);
  const grants = (planKey) => (fk) => (features[fk]?.plans || []).includes(planKey);

  const Tier = ({ planKey, highlight }) => {
    const p = plans[planKey];
    if (!p) return null;
    const mine = myPlan === planKey || (planKey === proKey && myPlan === "lifetime_pro");
    const check = grants(planKey);
    return (
      <div data-testid={`plan-${planKey}`} className={`relative flex flex-col rounded-3xl border p-6 ${highlight ? "border-emerald-500 bg-white shadow-lg shadow-emerald-100" : "border-zinc-200 bg-white"}`}>
        {highlight && <span className="absolute -top-3 left-6 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white">Most popular</span>}
        <div className="flex items-center gap-2">
          {highlight && <Crown className="h-5 w-5 text-amber-500" />}
          <h3 className="font-display text-xl font-bold text-zinc-900">{p.label}</h3>
        </div>
        <div className="mt-3 flex items-end gap-1">
          <span className="font-display text-4xl font-extrabold tracking-tight text-zinc-900">{p.price ? `${sym}${Number(p.price).toFixed(2)}` : "Free"}</span>
          {!!p.price && <span className="mb-1 text-sm text-zinc-400">/{p.interval === "lifetime" ? "lifetime" : "mo"}</span>}
        </div>
        <ul className="mt-5 flex-1 space-y-2.5">
          {relevant.map(([fk, f]) => {
            const on = check(fk);
            return (
              <li key={fk} className={`flex items-start gap-2 text-sm ${on ? "text-zinc-700" : "text-zinc-300"}`}>
                <Check className={`mt-0.5 h-4 w-4 shrink-0 ${on ? "text-emerald-600" : "text-zinc-200"}`} />
                <span>{f.label}</span>
              </li>
            );
          })}
        </ul>
        <div className="mt-6">
          {mine ? (
            <Button disabled data-testid={`plan-current-${planKey}`} variant="outline" className="w-full rounded-full">Your current plan</Button>
          ) : highlight ? (
            <Button data-testid={`plan-upgrade-${planKey}`} onClick={() => toast.info(paymentsOn ? "Checkout coming soon." : "Pro plans launch soon — you'll be able to upgrade here.")} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700">
              <Sparkles className="mr-2 h-4 w-4" /> {paymentsOn ? "Upgrade" : "Notify me"}
            </Button>
          ) : (
            <Button disabled variant="outline" className="w-full rounded-full">Included</Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl">Plans & pricing</h1>
          <p className="mx-auto mt-2 max-w-xl text-zinc-500">
            {audience === "business"
              ? "Unlock the full analytics and campaign intelligence experience for your business."
              : "Grow faster with advanced insights, media-kit tools and a Pro profile."}
          </p>
          {isPro && (
            <span data-testid="plans-pro-tag" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-700">
              <Crown className="h-4 w-4" /> You're on Pro
            </span>
          )}
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          <Tier planKey={freeKey} />
          <Tier planKey={proKey} highlight />
        </div>

        <div data-testid="plans-notice" className="mt-6 flex items-start gap-2 rounded-2xl border border-zinc-200 bg-white p-4 text-sm text-zinc-500">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" />
          {paymentsOn
            ? "Prices are shown in your local currency. You can cancel anytime."
            : "Payments aren't live yet — this is a preview of upcoming plans. Nothing is charged and your current access is unaffected."}
        </div>
      </div>
      <MobileNav />
    </div>
  );
}
